'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';

import type { Reference } from '@eliadmin/core/src/api/references/references-types';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Button } from '../ui/button';
import { LuPlus } from 'react-icons/lu';
import { LightboxComponent } from '@eliadmin/core/src/components/lightbox/lightbox-component';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { useState } from 'react';
import { Reveal } from '../ui/reveal';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export function ReferenceCard({
  reference,
  index,
  headingTag: HeadingTag = 'h3',
}: {
  reference: Reference;
  index: number;
  headingTag?: 'h2' | 'h3';
}) {
  const { isOpen, setIsOpen } = useDisclosure(false);
  const [imagesSorted, setImagesSorted] = useState<{ url: string; name: string }[]>([]);

  const { t } = useTranslations();

  const handleOpenGallery = (images: any, index: number) => () => {
    if (Array.isArray(images) && index >= 0 && index < images.length) {
      const newImages = [...images.slice(index), ...images.slice(0, index)];
      setImagesSorted(newImages);
      setIsOpen(true);
    } else if (images && !Array.isArray(images)) {
      setImagesSorted([images]);
      setIsOpen(true);
    }
  };

  const allImages = [...(reference.image ? [reference.image] : []), ...(reference.images || [])];

  return (
    <>
      <LightboxComponent isOpen={isOpen} setIsOpen={setIsOpen} images={imagesSorted} />
      <Reveal variant="bottom">
        <div
          className={cn(
            'flex w-full flex-col items-start justify-start gap-4 sm:gap-6 md:items-center md:justify-start md:gap-16 lg:gap-[72px]',
            {
              'md:flex-row': index % 2 === 0,
              'md:flex-row-reverse': index % 2 !== 0,
            },
          )}
        >
          <div className="relative h-[180px] w-full max-w-[680px] sm:h-[250px] lg:h-[380px] xl:h-[430px]">
            <Image
              src={reference.image.url}
              alt={resolveImageAlt(reference?.image, reference?.name)}
              fill
              className="absolute inset-0 z-0 h-full w-full object-cover object-center"
              sizes="(min-width: 1280px) 680px, (min-width: 768px) 60vw, 100vw"
            />
            {reference.images && reference.images.length > 0 && (
              <Button
                type="button"
                aria-label={`${t('text1944')}: ${reference.name}`}
                size={'custom'}
                variant={'secondary'}
                className="absolute bottom-4 right-4 z-10 h-11 w-11 p-0 sm:bottom-6 sm:right-6 sm:h-12 sm:w-auto sm:px-6 lg:bottom-8 lg:right-8 lg:h-14 lg:px-8"
                onClick={handleOpenGallery(allImages, 0)}
              >
                <p className="hidden sm:block">{t('text1944')}</p>{' '}
                <LuPlus className="h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
            )}
          </div>
          <div className="flex w-full max-w-[336px] flex-col items-start justify-start gap-4">
            {reference.categories && reference.categories.length > 0 && (
              <span className="rounded-full bg-primary-foreground px-4 py-1 text-xs font-normal text-black  sm:text-sm">
                {t('text1313')}:&nbsp;
                <strong className="font-bold">
                  {reference.categories.map(category => category.name).join(', ')}
                </strong>
              </span>
            )}
            <HeadingTag data-ea-visual-tag="h3" className="text-balance text-lg font-bold tracking-tighter text-black sm:text-xl lg:text-2xl xl:text-3xl">
              {reference.name}
            </HeadingTag>
            {reference.city && (
              <p className="text-sm sm:text-base md:text-lg lg:text-xl">{reference.city}</p>
            )}
          </div>
        </div>
      </Reveal>
    </>
  );
}
